import React, { useState, useEffect, useMemo } from "react";
import { Modal } from "@/components/common/Modal";
import { Button } from "@/components/common/Button";
import { Badge } from "@/components/common/Badge";
import { Permission, Role } from "@/contracts/auth-api.types";
import { roleService } from "@/services/roleService";
import { useNotification } from "@/hooks/useNotification";
import { Plus, Trash2, CheckCircle2, Shield, KeyRound } from "lucide-react";

interface RolePermissionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  role: Role | null;
  onSuccess: () => void;
}

export const RolePermissionsModal: React.FC<RolePermissionsModalProps> = ({
  isOpen,
  onClose,
  role,
  onSuccess,
}) => {
  const { notify } = useNotification();
  const [allPermissions, setAllPermissions] = useState<Permission[]>([]);
  const [rolePermissions, setRolePermissions] = useState<Permission[]>([]);
  const [selectedPermissionToAdd, setSelectedPermissionToAdd] = useState("");
  const [searchFilter, setSearchFilter] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    async function loadData() {
      if (!role) return;
      setIsLoading(true);
      try {
        const [all, attached] = await Promise.all([
          roleService.listPermissions(),
          roleService.listPermissions(role.id),
        ]);
        setAllPermissions(all || []);
        setRolePermissions(attached || role.permissions || []);
      } catch {
        // Fallback silencioso
      } finally {
        setIsLoading(false);
      }
    }

    if (isOpen && role) {
      loadData();
      setSelectedPermissionToAdd("");
      setSearchFilter("");
    }
  }, [isOpen, role]);

  const attachedIds = useMemo(() => new Set(rolePermissions.map((p) => p.id)), [rolePermissions]);
  const availableToAdd = useMemo(
    () => allPermissions.filter((p) => !attachedIds.has(p.id)),
    [allPermissions, attachedIds],
  );

  const filteredAttachedPermissions = useMemo(() => {
    if (!searchFilter.trim()) return rolePermissions;
    const term = searchFilter.toLowerCase();
    return rolePermissions.filter(
      (p) =>
        p.name.toLowerCase().includes(term) ||
        (p.description && p.description.toLowerCase().includes(term)),
    );
  }, [rolePermissions, searchFilter]);

  if (!role) return null;

  const handleAdd = async () => {
    if (!selectedPermissionToAdd) return;
    setIsProcessing(true);
    try {
      await roleService.addPermissionToRole(role.id, selectedPermissionToAdd);
      notify("Permissão adicionada com sucesso à role!", "success");
      const addedPerm = allPermissions.find((p) => p.id === selectedPermissionToAdd);
      if (addedPerm) {
        setRolePermissions((prev) => [...prev, addedPerm]);
      }
      setSelectedPermissionToAdd("");
      onSuccess();
    } catch (err: any) {
      notify(err.message || "Erro ao associar permissão", "error");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRemove = async (permissionId: string) => {
    setIsProcessing(true);
    try {
      await roleService.removePermissionFromRole(role.id, permissionId);
      notify("Permissão desvinculada com sucesso da role!", "success");
      setRolePermissions((prev) => prev.filter((p) => p.id !== permissionId));
      onSuccess();
    } catch (err: any) {
      notify(err.message || "Erro ao remover permissão", "error");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Gerenciar Permissões da Role: ${role.name}`}
      maxWidth="lg"
    >
      <div className="space-y-5">
        {/* Role context header card */}
        <div className="p-3.5 bg-gray-50 border border-gray-200 rounded-lg flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded bg-purple-100 text-purple-700 shrink-0">
              <Shield size={20} />
            </div>
            <div>
              <p className="text-xs text-muted">Perfil selecionado</p>
              <h3 className="text-base font-bold text-gray-900 font-mono">{role.name}</h3>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xs text-muted">Permissões Atribuídas</p>
            <p className="text-sm font-bold text-gray-900">
              {rolePermissions.length} de {allPermissions.length}
            </p>
          </div>
        </div>

        {/* Add Permission Bar */}
        <div>
          <label className="text-xs font-semibold uppercase text-muted tracking-wider block mb-1.5">
            Vincular Nova Permissão ao Perfil
          </label>
          <div className="bg-white border border-gray-200 p-2.5 rounded-lg flex flex-wrap items-center gap-2 shadow-xs">
            <select
              className="input-control flex-1 text-xs font-mono py-2"
              value={selectedPermissionToAdd}
              onChange={(e) => setSelectedPermissionToAdd(e.target.value)}
              disabled={isProcessing || availableToAdd.length === 0}
            >
              <option value="">
                {availableToAdd.length === 0
                  ? "Todas as permissões do catálogo já estão atribuídas"
                  : "Selecione uma permissão do catálogo para adicionar..."}
              </option>
              {availableToAdd.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} {p.description ? `— ${p.description}` : ""}
                </option>
              ))}
            </select>

            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={handleAdd}
              disabled={!selectedPermissionToAdd || isProcessing}
              isLoading={isProcessing}
            >
              <Plus size={15} />
              <span>Adicionar</span>
            </Button>
          </div>
        </div>

        {/* Current Permissions List */}
        <div>
          <div className="flex items-center justify-between gap-2 mb-2">
            <h4 className="text-xs font-semibold uppercase text-muted tracking-wider flex items-center gap-1.5">
              <KeyRound size={14} className="text-primary" />
              <span>Permissões Atribuídas ({rolePermissions.length})</span>
            </h4>

            {rolePermissions.length > 5 && (
              <div className="w-48">
                <input
                  type="text"
                  placeholder="Filtrar atribuídas..."
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                  className="input-control text-xs py-1 px-2"
                />
              </div>
            )}
          </div>

          {isLoading ? (
            <div className="py-8 text-center card">
              <div className="spinner mx-auto mb-2"></div>
              <p className="text-xs text-muted">Carregando permissões...</p>
            </div>
          ) : rolePermissions.length === 0 ? (
            <div className="p-4 bg-amber-50 border border-amber-200 text-amber-800 text-xs rounded-lg text-center">
              Esta role ainda não possui permissões atribuídas. Usuários vinculados a este perfil
              não terão privilégios operacionais.
            </div>
          ) : filteredAttachedPermissions.length === 0 ? (
            <div className="p-4 bg-gray-50 border border-gray-200 text-muted text-xs rounded-lg text-center">
              Nenhuma permissão vinculada encontrada com o filtro &quot;{searchFilter}&quot;.
            </div>
          ) : (
            <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
              {filteredAttachedPermissions.map((perm) => (
                <div
                  key={perm.id}
                  className="flex items-center justify-between p-2.5 bg-white border border-gray-200 rounded-lg hover:border-blue-200 hover:shadow-xs transition-colors"
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <CheckCircle2 size={16} className="text-green-600 shrink-0" />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <Badge variant="neutral" className="font-mono text-xs px-2 py-0.5">
                          {perm.name}
                        </Badge>
                      </div>
                      {perm.description && (
                        <p className="text-xs text-muted truncate mt-0.5">{perm.description}</p>
                      )}
                    </div>
                  </div>

                  <button
                    type="button"
                    className="btn-icon text-red-500 hover:text-red-700 hover:bg-red-50 ml-2 shrink-0"
                    onClick={() => handleRemove(perm.id)}
                    disabled={isProcessing}
                    title="Remover permissão da role"
                    aria-label={`Remover permissão ${perm.name}`}
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end pt-3 border-t border-gray-100">
          <Button variant="secondary" onClick={onClose}>
            Fechar
          </Button>
        </div>
      </div>
    </Modal>
  );
};
